import { Button, Container, Grid, Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { UseFormReturnType } from '@mantine/form/lib/use-form';

import { showNotification } from '@mantine/notifications';
import React, { ReactNode, useEffect } from 'react';
import { X } from 'tabler-icons-react';
import { UserManagementFilterMode } from '../../enums/misc.enum';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import { BasicMerchant } from '../../type/admin.types';
import { ErrorResponse } from '../../type/error-response.types';
import {
  SelectData,
  UserManagementFilterData,
  UserManagementFilterForm,
} from '../../type/filter.types';
import { BasicBranch } from '../../type/select.types';

const ResponsiveCol = ({ children }: { children: ReactNode }) => {
  return (
    <Grid.Col xs={12} sm={6}>
      {children}
    </Grid.Col>
  );
};

interface Props {
  filterValues: UseFormReturnType<UserManagementFilterForm>;
  setIsLoadMerchants: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadMerchants: boolean;
  filterSelectData: UseFormReturnType<UserManagementFilterData>;
  setSubmitApplied: React.Dispatch<React.SetStateAction<boolean>>;
  mode?: UserManagementFilterMode;
  isLoading: boolean;
}

function ResellerUserFilter(props: Props) {
  const {
    filterValues,
    setIsLoadMerchants,
    isLoadMerchants,
    filterSelectData,
    setSubmitApplied,
    mode,
    isLoading,
  } = props;
  const axiosPrivate = useAxiosPrivate();

  const selectLoadingData = useForm({
    initialValues: {
      isPartnersLoading: false,
      isResellersLoading: false,
      isMerchantsLoading: false,
      isBranchesLoading: false,
    },
  });

  const form = useForm({
    initialValues: filterValues.values,
  });

  const findValueInData = (data: SelectData[], value: string): boolean => {
    for (let i = 0; i < data?.length; i++) {
      if (data[i].value === value) {
        return true;
      }
    }
    return false;
  };

  const noData = 'No data';
  const loadingData = 'Loading...';

  useEffect(() => {
    const loadMerchants = async () => {
      selectLoadingData.setFieldValue('isMerchantsLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/merchants');

        const merchants = response.data?.items?.map(
          (merchant: BasicMerchant) => {
            return {
              label: merchant.merchantName,
              value: merchant.merchantId.toString(),
            };
          }
        );

        filterSelectData.setFieldValue('merchantsData', merchants);
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-merchants-data',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isMerchantsLoading', false);
      }
    };

    if (isLoadMerchants) {
      loadMerchants();
    } else {
      setIsLoadMerchants(true);
    }
  }, []);

  useDidMountEffect(() => {
    let ignore = false;

    const loadMerchantBranches = async () => {
      selectLoadingData.setFieldValue('isBranchesLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/branches', {
          params: {
            merchantId: form.values.merchant,
          },
        });

        const merchantBranches = response.data?.items?.map(
          (merchant: BasicBranch) => {
            return {
              label: merchant.branchName,
              value: merchant.branchId.toString(),
            };
          }
        );

        if (!ignore) {
          const found = findValueInData(
            merchantBranches,
            form.values.merchantBranch
          );

          if (!found) {
            form.setFieldValue('merchantBranch', '');
          }
          filterSelectData.setFieldValue(
            'merchantBranchesData',
            merchantBranches
          );
        }
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-branches-data',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isBranchesLoading', false);
      }
    };

    if (form.values.merchant) {
      loadMerchantBranches();
    } else {
      form.setFieldValue('merchantBranch', '');
      filterSelectData.setFieldValue('merchantBranchesData', []);
    }

    return () => {
      ignore = true;
    };
  }, [form.values.merchant]);

  const handleSubmit = () => {
    filterValues.setValues(form.values);
    setSubmitApplied((prev) => {
      return !prev;
    });
  };

  return (
    <>
      <Container fluid p={0} m={0}>
        <Grid>
          <ResponsiveCol>
            <Select
              label='Select Merchant'
              data={
                selectLoadingData.values.isMerchantsLoading
                  ? [loadingData]
                  : filterSelectData.values.merchantsData?.length === 0
                  ? [noData]
                  : filterSelectData.values.merchantsData
              }
              value={
                selectLoadingData.values.isMerchantsLoading
                  ? loadingData
                  : filterSelectData.values.merchantsData?.length === 0
                  ? noData
                  : form.values.merchant
              }
              disabled={
                selectLoadingData.values.isMerchantsLoading ||
                filterSelectData.values.merchantsData?.length === 0
              }
              onChange={(value: string) =>
                form.setFieldValue('merchant', value)
              }
              clearable={
                !(
                  selectLoadingData.values.isMerchantsLoading ||
                  filterSelectData.values.merchantsData?.length === 0
                )
              }
              searchable
            />
          </ResponsiveCol>
          {mode !== UserManagementFilterMode.APPROVE_ENTITY_MODE && (
            <ResponsiveCol>
              <Select
                label='Select Merchant Branch'
                data={
                  selectLoadingData.values.isBranchesLoading
                    ? [loadingData]
                    : filterSelectData.values.merchantBranchesData?.length ===
                        0 && form.values.merchant
                    ? [noData]
                    : filterSelectData.values.merchantBranchesData
                }
                value={
                  selectLoadingData.values.isBranchesLoading
                    ? loadingData
                    : filterSelectData.values.merchantBranchesData?.length ===
                        0 && form.values.merchant
                    ? noData
                    : form.values.merchantBranch
                }
                disabled={
                  selectLoadingData.values.isBranchesLoading ||
                  filterSelectData.values.merchantBranchesData?.length === 0 ||
                  form.values.merchant?.length === 0
                }
                onChange={(value: string) =>
                  form.setFieldValue('merchantBranch', value)
                }
                clearable={
                  !(
                    selectLoadingData.values.isBranchesLoading ||
                    filterSelectData.values.merchantBranchesData?.length ===
                      0 ||
                    form.values.merchant?.length === 0
                  )
                }
                searchable
              />
            </ResponsiveCol>
          )}
        </Grid>
        <Button
          mt='lg'
          type='submit'
          onClick={handleSubmit}
          disabled={isLoading}
        >
          Apply
        </Button>
      </Container>
    </>
  );
}

export default ResellerUserFilter;
